import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { forwardRef } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const InputWrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.textDark};
  text-align: right;
`;

const StyledInput = styled.input.attrs<{ hasError?: boolean; id?: string }>(({ hasError, id, ...props }) => ({
  ...props,
  id,
}))<{ hasError?: boolean }>`
  padding: ${theme.spacing.md};
  font-size: ${theme.fontSizes.md};
  border: 2px solid ${({ hasError }) => hasError ? theme.colors.secondary : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.background};
  color: ${theme.colors.text};
  transition: all ${theme.transitions.fast};
  direction: rtl;
  text-align: right;
  font-family: ${theme.fonts.primary};

  &::placeholder {
    color: ${theme.colors.textLight};
    text-align: right;
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => hasError ? theme.colors.secondary : theme.colors.secondary};
    box-shadow: 0 0 0 3px ${({ hasError }) => 
      hasError ? 'rgba(242, 180, 141, 0.2)' : 'rgba(242, 180, 141, 0.15)'};
  }

  &:disabled {
    background-color: ${theme.colors.backgroundDark};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &[type="email"],
  &[type="url"] {
    direction: ltr;
    text-align: left;
  }
`;

const ErrorText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.secondary};
  text-align: right;
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  text-align: right;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, fullWidth, id, ...props }, ref) => {
    // Generate a unique ID if not provided
    const inputId = id || `input-${label?.replace(/\s+/g, '-').toLowerCase()}`;
    
    return (
      <InputWrapper fullWidth={fullWidth}>
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <StyledInput ref={ref} id={inputId} hasError={!!error} {...props} />
        {error && <ErrorText>{error}</ErrorText>}
        {!error && helperText && <HelperText>{helperText}</HelperText>}
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';
